import { Card, CardContent, Typography, Box } from '@mui/material'
import { alpha, keyframes, useTheme } from '@mui/material/styles'
import { tokens } from '../../../theme'

const countUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`

/**
 * KPICard — Radial SVG progress ring for percentage metrics
 * Use for completion rates, coverage, or any 0-100% metric
 */
const KPICard = ({
  title,
  value,
  label,
  subtitle,
  icon,
  color = '#63B3ED',
  onClick
}) => {
  const theme = useTheme()
  const palette = theme.palette

  const radius = 28
  const strokeWidth = 5

  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: `all ${tokens.transitions.normal}`,
        position: 'relative',
        overflow: 'hidden',
        bgcolor: palette.background.paper,
        border: `1px solid ${palette.divider}`,
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          borderColor: alpha(color, 0.3),
          boxShadow: `0 4px 16px rgba(0,0,0,0.15), 0 0 20px ${alpha(color, 0.1)}`
        } : {
          borderColor: alpha(palette.text.primary, 0.1)
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.4)} 100%)`,
          opacity: 0.8
        }
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, pb: { xs: '16px !important', sm: '20px !important' } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Title */}
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: '0.7rem',
                letterSpacing: '0.1em',
                lineHeight: 1.5,
                display: 'block',
                mb: 0.75
              }}
            >
              {title}
            </Typography>

            {/* Value */}
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontWeight: 700,
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                color: 'text.primary',
                lineHeight: 1,
                letterSpacing: '-0.02em',
                animation: `${countUp} 0.5s ease-out`
              }}
            >
              {label || value}
            </Typography>

            {/* Subtitle */}
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                mt: 1,
                fontSize: '0.75rem',
                lineHeight: 1.4
              }}
            >
              {subtitle}
            </Typography>
          </Box>

          {/* Radial Progress */}
          <Box
            sx={{
              position: 'relative',
              ml: { xs: 1, sm: 1.5 },
              flexShrink: 0,
              width: 64,
              height: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg width={64} height={64} viewBox="0 0 64 64">
              <circle
                cx={32}
                cy={32}
                r={radius}
                fill="none"
                stroke={alpha(color, 0.25)}
                strokeWidth={strokeWidth}
              />
            </svg>
            {/* Center icon or percentage */}
            <Box
              sx={{
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& .MuiSvgIcon-root': {
                  color: color,
                  fontSize: 20
                }
              }}
            >
              {icon}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

export default KPICard
